import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { OverviewCard } from '@ctw/shared/components/containers/overview-card';
import type { LegacyMinRecordItem } from '@ctw/shared/components/tables/legacy-table';
import { useGroupedBy } from '@ctw/shared/content/hooks/use-grouped-by';
import type { ResourceOverviewCardProps } from '@ctw/shared/content/overview/resource-overview-card';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { twx } from '@ctw/shared/utils/tailwind';
import type { Resource } from 'fhir/r4';
import { isFunction } from 'lodash-es';
import { useToggleRead } from '../hooks/use-toggle-read';

export interface ResourceOverviewCardGroupedProps<T extends LegacyMinRecordItem>
  extends ResourceOverviewCardProps<T> {
  groupBy: (record: T) => string;
  groupByKeyOrder?: Array<string>;
  titleClassName?: string;
}

export const ResourceOverviewCardGrouped = <T extends Resource, M extends FHIRModel<T>>({
  renderResource,
  data = [],
  onRowClick,
  rowClassName,
  groupBy,
  groupByKeyOrder,
  footerCTA,
  titleClassName,
  ...overviewCardProps
}: ResourceOverviewCardGroupedProps<M>) => {
  const telemetry = useTelemetry();
  const { requestContext } = useCTW();
  const { toggleRead } = useToggleRead();
  // Group the records using the groupBy function
  const { groupedRecords, sortedKeys } = useGroupedBy(groupBy, data, groupByKeyOrder);

  function handleRowClick(record: M) {
    if (onRowClick) {
      if (!(record.isRead || record.ownedByBuilder(requestContext.builderId))) {
        void toggleRead(record);
      }

      onRowClick({ telemetry, record });
      telemetry.trackInteraction('table_row_left_click', {
        tableRowResourceType: record.resourceType,
        tableRowResourceId: record.id,
      });
    }
  }

  return (
    <OverviewCard
      {...overviewCardProps}
      empty={data.length === 0}
      fullbleed={true}
      footerCTA={
        footerCTA
          ? {
              label: footerCTA.label,
              onClick: () => {
                footerCTA.onClick();
              },
            }
          : undefined
      }
    >
      {sortedKeys.map((titleKey) => (
        <div data-testid="overview-group-row" key={titleKey}>
          <div
            className={twx(
              'bg-background-disabled px-4 pt-2 pb-1 text-content-subtle text-xs uppercase',
              titleClassName,
            )}
          >
            {titleKey}
          </div>
          {(groupedRecords[titleKey] ?? []).map((record) => (
            <div
              className={twx('px-2 py-1.5', rowClassName, {
                'cursor-pointer hover:bg-background-hover': isFunction(onRowClick),
                'last:rounded-b-lg': !footerCTA,
                '!text-content-subtle': record.isDismissed,
              })}
              data-testid="resource-overview-row"
              key={record.key}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRowClick(record);
                }
              }}
              onClick={() => handleRowClick(record)}
            >
              {renderResource(record)}
            </div>
          ))}
        </div>
      ))}
    </OverviewCard>
  );
};
